import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect } from 'react';

import { Team, useGetTeamQuery } from '@graphql/generated';
import { SESSION_SELECTED_TEAM_NAME } from '@src/constants/constants';
// TA-10850: the same not-found verdict the errorLink and the team screens
// use. The old `message.includes('could not be found')` check could not see
// the HTTP-401 form of the answer, whose top-level message is only
// "Response not successful: Received status code 401".
import { isApolloResourceNotFound } from '@utils/isResourceNotFoundError';

import { useApolloClient } from './useApolloClient';
/**
 * Retrieves a project for the provided `projectId`
 */
export const useTeamFromQuery = ({
  teamUUId,
  fetchPolicy = 'cache-and-network',
  includePending = false,
  withMembers = true,
  shouldClearSelectedTeamOnNotFound = false,
}: {
  teamUUId?: Team['uuid'];
  includePending?: boolean;
  /**
   * Select TeamMembersDetail (users / invitations / invoices). Pass false
   * from readers that never render those lists (TA-10847).
   */
  withMembers?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  /**
   * Clear the persisted selected-team name when this team turns out not to
   * exist. Opt-in, and off by default — the same rule useTeamDetailFromQuery
   * follows: this is a generic reader, and most of its call sites (invitation
   * previews, member pickers, subscription stacks) look up a team they do not
   * own the drawer selection for. A stale uuid there must not blank out the
   * selection that belongs to another route.
   *
   * The stale cache entry is evicted either way: that is local to the record
   * this hook just failed to read, not global navigation state.
   */
  shouldClearSelectedTeamOnNotFound?: boolean;
}) => {
  const { client } = useApolloClient();

  const {
    data: { getTeam } = {},
    loading,
    error,
    refetch,
  } = useGetTeamQuery({
    variables: {
      id: teamUUId || '',
      includePending: includePending || false,
      withMembers,
    },
    fetchPolicy,
    skip: !teamUUId,
  });

  useEffect(() => {
    if (isApolloResourceNotFound(error)) {
      if (
        shouldClearSelectedTeamOnNotFound &&
        typeof window !== 'undefined' &&
        window.sessionStorage
      ) {
        window.sessionStorage.removeItem(SESSION_SELECTED_TEAM_NAME);
      }

      if (client && teamUUId) {
        const cacheId = client.cache.identify({
          __typename: 'Team',
          uuid: teamUUId,
        });
        if (cacheId) {
          client.cache.evict({ id: cacheId });
          client.cache.gc();
        }
      }
    }
  }, [error, teamUUId, client, shouldClearSelectedTeamOnNotFound]);

  return { team: getTeam as Team | undefined, loading, error, refetch };
};
